import _ from "lodash";
/** External */
import moment from "moment-timezone";

/** Internal */
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import {
  getDateRangeForCurrentAYOption,
  getDateRangeForPreviousAYOption,
  getDateRangeForAcademicYear,
  getOrganizationGlobalConstantsQueryVariablesMemoized,
  getCurrentAcademicYearStartDate,
  getDateRangeOfGradingPeriod,
  getDateRangeOfAcademicYear,
} from "./ToddleAnalyticsUtils";
import {
  LAST_7_DAYS,
  LAST_30_DAYS,
  CUSTOM_DURATION,
  CURRENT_ACADEMIC_YEAR,
  PREVIOUS_ACADEMIC_YEAR,
  GRADING_PERIOD,
  PAST_ACADEMIC_YEARS,
  LAST_6_MONTHS,
  LAST_90_DAYS,
} from "ToddleAnalytics/modules/Constants";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getOrganizationGlobalConstantsFromCache } from "modules/CommonGraphqlHelpers";

/**
 * Retrieves the current time zone value based on the user's organization.
 * @returns {string} The current time zone value.
 */
export const getCurrentTimeZoneValue = () => {
  const { org_id } = getUserInfo({ portalType: "PLANNER" });
  const globalAnalyticsConstantsQueryVariables = getOrganizationGlobalConstantsQueryVariablesMemoized(
    org_id
  );
  const timezoneData = getOrganizationGlobalConstantsFromCache(
    globalAnalyticsConstantsQueryVariables
  );
  const globalConstants = _.get(timezoneData, "node.globalConstants");
  const timeZoneObject = _.find(globalConstants, ({ name }) =>
    _.isEqual(name, "TIMEZONE")
  );

  return _.get(timeZoneObject, "value");
};

/**
 * Converts a date to a moment object using the school's time zone.
 * @param {string|Date|moment.Moment} date - The date to convert.
 * @param {boolean} [keepLocalTime=false] - Whether to keep the local time when converting.
 * @returns {moment.Moment} The moment object with the school's time zone applied.
 */
export const getMomentDateWithSchoolTimeZone = (
  date,
  keepLocalTime = false
) => {
  const timeZone = getCurrentTimeZoneValue();
  return timeZone ? moment(date).tz(timeZone, keepLocalTime) : moment(date);
};

/**
 * Checks if two dates are the same.
 * @param {moment.Moment} date1 - The first date to compare.
 * @param {moment.Moment} date2 - The second date to compare.
 * @param {Object} [options] - Options for comparison.
 * @param {boolean} [options.compareTime=true] - Whether to compare time as well as date.
 * @returns {boolean} True if the dates (and optionally times) are the same, false otherwise.
 */
export const isSameDate = (date1, date2, { compareTime = true } = {}) => {
  if (!date1.isValid() || !date2.isValid()) {
    return false;
  }
  if (compareTime) {
    return date1.isSame(date2);
  } else {
    const formatWithoutTime = DATE_TIME_CONSTANTS.DD_MM_YYYY;
    const date1WithoutTime = date1.format(formatWithoutTime);
    const date2WithoutTime = date2.format(formatWithoutTime);
    return _.isEqual(date1WithoutTime, date2WithoutTime);
  }
};

/**
 * Formats a given date into a specified format.
 * @param {Object} params - The parameters for the function.
 * @param {Date|string} params.date - The date to format. It can be a Date object or a string that can be parsed as a date.
 * @param {string} [params.format="D MMMM, YYYY"] - The format string used by moment.js to format the date. Default is "D MMMM, YYYY".
 * @returns {string} The formatted date string.
 */
export const getFormattedDate = ({ date, format = "D MMMM, YYYY" }) =>
  moment(date).format(format);

export const getFormattedDateWithWeekday = date => {
  if (!date) return "-";
  return getMomentDateWithSchoolTimeZone(date, true).format("dddd, DD MMM, YY");
};

/**
 * Calculates the number of days between two dates, inclusive.
 * @param {Object} range - The date range.
 * @param {string|Date|moment.Moment} range.startDate - The start date.
 * @param {string|Date|moment.Moment} range.endDate - The end date.
 * @returns {number} The number of days in the range.
 */
export const calculateDaysInRange = ({ startDate, endDate }) => {
  const momentStartDate = moment(startDate);
  const momentEndDate = moment(endDate);
  return momentEndDate.diff(momentStartDate, "days") + 1;
};

const dateRangeDaysMap = {
  [LAST_7_DAYS]: 7,
  [LAST_30_DAYS]: 30,
  [LAST_90_DAYS]: 90,
  [LAST_6_MONTHS]: 180,
};

/**
 * Retrieves the start and end date for a given date range type.
 * @param {Object} dateRange - The date range object.
 * @param {string} dateRange.dateViewType - The type of date range (e.g., LAST_7_DAYS, CUSTOM_DURATION).
 * @param {string|Date|moment.Moment} [dateRange.rangeStartDate] - The start date for custom ranges.
 * @param {string|Date|moment.Moment} [dateRange.rangeEndDate] - The end date for custom ranges.
 * @returns {Object} The start and end date of the range.
 */
export const getDateRange = dateRange => {
  const {
    dateViewType,
    rangeStartDate,
    rangeEndDate,
    dateViewAcademicYear,
    dateViewGradingPeriod,
  } = dateRange;

  const daysToSubtractFromCurrentDate = dateRangeDaysMap[dateViewType];
  if (daysToSubtractFromCurrentDate) {
    let rangeStart = getMomentDateWithSchoolTimeZone()
      .startOf("day")
      .subtract(daysToSubtractFromCurrentDate, "days");

    // If the start date of the range is earlier than the start date of the current academic year,
    // set it to the current academic year start date.
    const currentAYStartDate = getMomentDateWithSchoolTimeZone(
      getCurrentAcademicYearStartDate(),
      true
    ).startOf("day");

    if (currentAYStartDate.isAfter(rangeStart)) {
      rangeStart = currentAYStartDate;
    }

    rangeStart = rangeStart.format();

    const rangeEnd = getMomentDateWithSchoolTimeZone()
      .endOf("day")
      .subtract(1, "day")
      .format();

    return {
      rangeStart,
      rangeEnd,
    };
  }

  if (dateViewType === CUSTOM_DURATION) {
    return {
      rangeStart: getMomentDateWithSchoolTimeZone(rangeStartDate, true)
        .startOf("day")
        .format(),
      rangeEnd: getMomentDateWithSchoolTimeZone(rangeEndDate, true)
        .endOf("day")
        .format(),
    };
  }

  if (dateViewType === CURRENT_ACADEMIC_YEAR) {
    const { startDate, endDate } = getDateRangeForCurrentAYOption();
    return {
      rangeStart: startDate.format(),
      rangeEnd: endDate.format(),
    };
  }

  if (dateViewType === PREVIOUS_ACADEMIC_YEAR) {
    const { startDate, endDate } = getDateRangeForPreviousAYOption();
    return {
      rangeStart: startDate.format(),
      rangeEnd: endDate.format(),
    };
  }

  if (dateViewType === PAST_ACADEMIC_YEARS) {
    const { startDate, endDate } = getDateRangeForAcademicYear({
      academicYearId: dateViewAcademicYear,
    });

    return {
      rangeStart: startDate.format(),
      rangeEnd: endDate.format(),
    };
  }

  if (dateViewType === GRADING_PERIOD) {
    const { startDate, endDate } = getDateRangeOfGradingPeriod(
      dateViewGradingPeriod
    );

    // If the end date of the grading period is greater than (today-1), set end date to (today-1).
    const maxEndDate = getMomentDateWithSchoolTimeZone()
      .subtract(1, "day")
      .endOf("day");

    let rangeEnd = endDate;
    if (maxEndDate.isBefore(endDate)) {
      rangeEnd = maxEndDate;
    }

    return {
      rangeStart: startDate.format(),
      rangeEnd: rangeEnd.format(),
    };
  }

  if (dateViewType === PAST_ACADEMIC_YEARS) {
    const { startDate, endDate } = getDateRangeOfAcademicYear(
      dateViewAcademicYear
    );
    return {
      rangeStart: startDate.format(),
      rangeEnd: endDate.format(),
    };
  }

  // Return an empty object if the key doesn't match any range
  return {};
};

/**
 * Gets the number of days for a specific date range type.
 * @param {Object} dateRange - The date range object.
 * @param {string} dateRange.dateViewType - The type of date range (e.g., LAST_7_DAYS, CUSTOM_DURATION).
 * @param {string|Date|moment.Moment} [dateRange.rangeStartDate] - The start date for custom ranges.
 * @param {string|Date|moment.Moment} [dateRange.rangeEndDate] - The end date for custom ranges.
 * @returns {number|null} The number of days in the range, or null if the range type is not recognized.
 */
export const getNumberOfDays = ({
  dateViewType,
  rangeStartDate,
  rangeEndDate,
}) => {
  switch (dateViewType) {
    case LAST_7_DAYS:
      return 7;
    case LAST_30_DAYS:
      return 30;
    case LAST_90_DAYS:
      return 90;
    case CUSTOM_DURATION: {
      return calculateDaysInRange({
        startDate: rangeStartDate,
        endDate: rangeEndDate,
      });
    }
    case CURRENT_ACADEMIC_YEAR: {
      const { startDate, endDate } = getDateRangeForCurrentAYOption();
      return calculateDaysInRange({
        startDate,
        endDate,
      });
    }
    case PREVIOUS_ACADEMIC_YEAR: {
      const { startDate, endDate } = getDateRangeForPreviousAYOption();
      return calculateDaysInRange({
        startDate,
        endDate,
      });
    }
    default:
      return null;
  }
};

export const getYearDurationFormat = ({ startDate, endDate }) => {
  const startMomentDate = moment(startDate);
  const endMomentDate = moment(endDate);

  const startYear = startMomentDate.format("YYYY");
  const endYear = endMomentDate.format("YY");

  return startMomentDate.year() !== endMomentDate.year()
    ? `${startYear}-${endYear}`
    : startYear;
};

export const getDateDiffMinutes = (startDate, endDate) => {
  return moment(endDate).diff(moment(startDate), "minutes");
};
